import React from 'react';
import { classNames } from 'primereact/utils';
import { Skeleton } from 'primereact/skeleton';
import { FormField } from '../../FormField/FormField';
import { UserDisplay } from '../../UserDisplay/UserDisplay';
import { Switch } from '../../Switch/Switch';
import { FormFieldMessage } from '../../ConditionalFieldMessage/ConditionalFieldMessage';
import { CouponDisplay } from '../../CouponDisplay/CouponDisplay';
import { AdaptiveSelect } from '../../AdaptiveSelect/AdaptiveSelect';
import { RadioButtonGroup } from '../../RadioButtonGroup/RadioButtonGroup';
import { getOrgCouponOptions, getUserCouponOptions, getUserOptions } from '../../../utils/selectOptions';
import { Reservation } from '../../../types/reservation';
import { ExternalUserWithCoupons } from '../../../types/externalUser';
import { Coupon, UserCouponWithCouponAndUsesCount } from '../../../types/coupon';
import { TypeOfCouponToUse } from '../validators';
import { ReservationFormFieldProps } from '../types';

interface Props extends ReservationFormFieldProps {
  resource: Reservation | undefined;
  isModifying: boolean;
  hasSelectedExternalUsers: boolean;
  activeUserCoupons: UserCouponWithCouponAndUsesCount[] | undefined;
  activeUserCouponsLoading: boolean;
  orgCoupons: Coupon[] | undefined;
  orgCouponsLoading: boolean;
  usersWithActiveCoupons: ExternalUserWithCoupons[] | undefined;
  usersWithActiveCouponsLoading: boolean;
  couponSelectionDataLoading: boolean;
  selectedUserCoupon: UserCouponWithCouponAndUsesCount | undefined;
  selectedUserCouponLoading: boolean;
  /** The coupon the saved reservation used, kept while the panel is open. */
  reservationUserCoupon: UserCouponWithCouponAndUsesCount | undefined;
  renderCouponNote: () => React.ReactNode;
}

/** Pay the session with a coupon: use an existing one, buy a new one or share one. */
export const CouponField = ({
  form,
  setFormFields,
  validationErrors,
  resource,
  isModifying,
  hasSelectedExternalUsers,
  activeUserCoupons,
  activeUserCouponsLoading,
  orgCoupons,
  orgCouponsLoading,
  usersWithActiveCoupons,
  usersWithActiveCouponsLoading,
  couponSelectionDataLoading,
  selectedUserCoupon,
  selectedUserCouponLoading,
  reservationUserCoupon,
  renderCouponNote,
}: Props) => {
  const hasActiveCoupons = activeUserCoupons?.length;

  return (
    <FormField
      labelTitle='Bono'
      elementId='reservation-coupon'
      fullWidth
      error={validationErrors?.newCouponId || validationErrors?.selectedUserToShareCoupon}
    >
      {hasSelectedExternalUsers && isModifying && (
        <Switch
          id='reservation-coupon'
          label='Pagar sesión con bono'
          className={classNames('reservation-coupon-toggle', {
            inactive: !form?.useCoupon,
          })}
          primeSwitchProps={{
            checked: !!form?.useCoupon,
            onChange: (e) => {
              const isChecked = e.value;

              let price = undefined;
              let userCouponId = undefined;
              let typeOfCouponToUse: TypeOfCouponToUse | undefined = undefined;
              if (isChecked && (hasActiveCoupons || resource?.userCouponId)) {
                price = resource?.userCouponId ? resource?.price : 0;
              }
              if (isChecked) {
                if (resource?.userCouponId) {
                  userCouponId = resource?.userCouponId;
                  typeOfCouponToUse = 'existing';
                } else {
                  userCouponId = undefined;
                  if (hasActiveCoupons) {
                    userCouponId = activeUserCoupons[0].id;
                  } else {
                    typeOfCouponToUse = 'new';
                  }
                }
              }

              setFormFields({
                useCoupon: isChecked,
                price,
                userCouponId,
                newCouponId: undefined,
                typeOfCouponToUse,
                selectedUserToShareCoupon: undefined,
              });
            },
            invalid: !!validationErrors?.useCoupon,
          }}
        />
      )}
      {hasSelectedExternalUsers && form?.useCoupon && (
        <>
          {!isModifying && resource?.userCouponId && (
            <CouponDisplay coupon={selectedUserCoupon} loading={selectedUserCouponLoading} />
          )}
          {isModifying &&
            (couponSelectionDataLoading ? (
              <>
                <div className='radio-button-group-container'>
                  <Skeleton height='112px' width='50%' />
                </div>
                <Skeleton height='48px' />
                <Skeleton height='34.5px' />
              </>
            ) : (
              <>
                <div className='radio-button-group-container'>
                  <span className='radio-button-group-title'>Elige cómo quieres asignar el bono:</span>
                  <RadioButtonGroup
                    name='type-of-coupon-to-use'
                    options={[
                      {
                        value: 'existing',
                        label: 'Usar bono existente',
                        id: 'existing',
                        disabled: !activeUserCoupons?.length && !resource?.userCouponId,
                      },
                      { value: 'new', label: 'Crear nuevo bono', id: 'new', disabled: !orgCoupons?.length },
                      {
                        value: 'share',
                        label: 'Compartir bono',
                        id: 'share',
                        disabled: !usersWithActiveCoupons?.length,
                      },
                    ]}
                    value={form.typeOfCouponToUse ?? (form.userCouponId ? 'existing' : 'new')}
                    onChange={(value) => {
                      if (value === 'existing') {
                        const couponToSelect = resource?.userCouponId ?? activeUserCoupons?.[0]?.id;
                        setFormFields({
                          userCouponId: couponToSelect,
                          price: resource?.userCouponId ? resource?.price : 0,
                          newCouponId: undefined,
                          typeOfCouponToUse: value,
                          selectedUserToShareCoupon: undefined,
                        });
                      }
                      if (value === 'new') {
                        setFormFields({
                          userCouponId: undefined,
                          price: undefined,
                          typeOfCouponToUse: value,
                          selectedUserToShareCoupon: undefined,
                        });
                      }
                      if (value === 'share') {
                        setFormFields({
                          newCouponId: undefined,
                          price: undefined,
                          typeOfCouponToUse: value,
                          selectedUserToShareCoupon: undefined,
                        });
                      }
                    }}
                    layout='vertical'
                  />
                </div>
                {(form.typeOfCouponToUse === 'existing' || (!form.typeOfCouponToUse && form.userCouponId)) && (
                  <AdaptiveSelect
                    id='user-active-coupon-select'
                    className='user-active-coupon-select'
                    options={getUserCouponOptions(activeUserCoupons, reservationUserCoupon)}
                    itemTemplate={(option: UserCouponWithCouponAndUsesCount) => <CouponDisplay coupon={option} hideInfoIcon />}
                    valueTemplate={(option: UserCouponWithCouponAndUsesCount) => <CouponDisplay coupon={option} />}
                    value={form?.userCouponId}
                    onChange={(value) => {
                      setFormFields({
                        userCouponId: value,
                        price: resource?.userCouponId === value ? resource?.price : 0,
                      });
                    }}
                    loading={activeUserCouponsLoading}
                  />
                )}
                {(form.typeOfCouponToUse === 'new' || (!form.typeOfCouponToUse && !form.userCouponId)) && (
                  <AdaptiveSelect
                    id='org-coupon-select'
                    options={getOrgCouponOptions(orgCoupons)}
                    value={form?.newCouponId}
                    onChange={(value) => {
                      const price = value ? orgCoupons?.find((coupon) => coupon.id === value)?.price : undefined;
                      setFormFields({ newCouponId: value, price });
                    }}
                    invalid={!!validationErrors?.newCouponId}
                    emptyMessage='No hay bonos'
                    emptyFilterMessage='No se han encontrado bonos'
                    placeholder='Elige un bono'
                    loading={orgCouponsLoading}
                  />
                )}
                {form.typeOfCouponToUse === 'share' && (
                  <AdaptiveSelect
                    id='user-to-share-coupon-select'
                    options={getUserOptions(usersWithActiveCoupons)}
                    value={form.selectedUserToShareCoupon?.id}
                    onChange={(value) => {
                      const foundUser = usersWithActiveCoupons?.find((user) => user.id === value);
                      if (foundUser) {
                        setFormFields({
                          userCouponId: foundUser.userCoupons?.[0]?.id,
                          price: 0,
                          selectedUserToShareCoupon: foundUser,
                        });
                      }
                    }}
                    itemTemplate={(option) => {
                      if (!option) return null;
                      return <UserDisplay fullName={option.label} avatarUrl={option.avatarUrl} />;
                    }}
                    invalid={!!validationErrors?.selectedUserToShareCoupon}
                    emptyMessage='No hay clientes con bonos activos'
                    emptyFilterMessage='No se han encontrado clientes'
                    placeholder='Elige un cliente'
                    loading={usersWithActiveCouponsLoading}
                  />
                )}
                {form.typeOfCouponToUse === 'share' && form.selectedUserToShareCoupon && (
                  <AdaptiveSelect
                    id='user-active-coupon-select'
                    className='user-active-coupon-select'
                    options={getUserCouponOptions(form.selectedUserToShareCoupon.userCoupons)}
                    itemTemplate={(option: UserCouponWithCouponAndUsesCount) => <CouponDisplay coupon={option} hideInfoIcon />}
                    valueTemplate={(option: UserCouponWithCouponAndUsesCount) => <CouponDisplay coupon={option} />}
                    value={form?.userCouponId}
                    invalid={!!validationErrors?.userCouponId}
                    onChange={(value) => {
                      setFormFields({
                        userCouponId: value,
                        price: resource?.userCouponId === value ? resource?.price : 0,
                      });
                    }}
                    loading={activeUserCouponsLoading}
                  />
                )}
              </>
            ))}
        </>
      )}
      {renderCouponNote()}
      {!hasSelectedExternalUsers && <FormFieldMessage message='Selecciona un cliente' />}
    </FormField>
  );
};
